import {OverlayRef} from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {getLongestProductionSchedulePath} from '@application/helper/production-schedule-builder';
import {DimensionsInPx} from '@domain/dimensions-in-px';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {FreeZone} from '@domain/production-schedule/free-zone/free-zone';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDisplay} from '@domain/production-schedule/production-schedule-display.enum';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {RestZone} from '@domain/production-schedule/rest-zone/rest-zone';
import {Permission} from '@domain/profile/permission.enum';
import {PropertyValue} from '@domain/property-value';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ProductionScheduleZoneComponent} from '@presentation/pages/texfab/production-schedule/add/plan/builder/free-and-rest-zones/production-schedule-zone.component';
import {ProductionScheduleColoredYarnSetComponent} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-colored-yarn-set/production-schedule-colored-yarn-set.component';
import {Zone} from '@presentation/pages/texfab/production-schedule/add/plan/builder/zone';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {
  AssertionUtils,
  BaseComponent,
  convertCommercialUnit,
  convertDentsToWidthInMeter,
  convertPicksToHeightInMeter,
  DistanceSpecifierOrientation,
  EnumUtils,
  MathUtils,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {axisBottom as d3AxisBottom} from 'd3-axis';
import {scaleLinear as d3ScaleLinear} from 'd3-scale';
import {BaseType, select as d3Select, Selection as d3Selection} from 'd3-selection';
import {cloneDeep} from 'lodash-es';
import {Observable, Observer, Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {ProductionScheduleBuilderAspectRatio} from '../builder/production-schedule-builder-aspect-ratio';
import {ProductionSchedulePathOfColoredYarnSetNewComponent} from '../builder/production-schedule-colored-yarn-set/production-schedule-path-of-colored-yarn-set/production-schedule-path-of-colored-yarn-set-new.component';
import {ColorListPreviewWithPositionComponent} from '../color-list-preview-with-position/color-list-preview-with-position.component';

@Component({
  selector: 'app-production-schedule-verify',
  templateUrl: './production-schedule-verify.component.html',
  styleUrls: ['./production-schedule-verify.component.scss']
})
export class ProductionScheduleVerifyComponent extends BaseComponent implements AfterViewInit, OnDestroy, OnChanges {
  public static readonly minProductionScheduleVerifyHeightInPicks = 5000;
  public static readonly dentOffset = 1;

  @Input() public productionSchedule: ProductionSchedule;
  @Input() public commercialUnit: Unit;
  @Input() public listOfCustomSettings: PropertyValue[];
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public zoomLevel;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public buggyLengthMaxInMM: number;
  @Input() public buggyLengthMinInPicks: number;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public selectedProductionScheduleDisplay: ProductionScheduleDisplay;
  @Input() public displayTechnicalUnit = true;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public availableSpaceInBuggyInCommercialUnit: number;
  @Output() public canZoomIn: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public canZoomOut: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public productionSchedulePlanChanged: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('productionScheduleVerifyContainer') public productionScheduleVerifyContainer: ElementRef;
  @ViewChild('productionScheduleVerify') public productionScheduleVerify: ElementRef;
  @ViewChild('productionScheduleVerifyGroup') public productionScheduleVerifyGroup: ElementRef;
  @ViewChild('productionScheduleVerifyVerticalAxis') public productionScheduleVerifyVerticalAxis: ElementRef;
  @ViewChild('productionScheduleVerifyHorizontalAxis') public productionScheduleVerifyHorizontalAxis: ElementRef;
  @ViewChild(ProductionScheduleZoneComponent) public productionScheduleZoneComponent: ProductionScheduleZoneComponent;
  @ViewChild('topScrollIndicator') public topScrollIndicator: ElementRef;
  @ViewChild('bottomScrollIndicator') public bottomScrollIndicator: ElementRef;
  @ViewChild('actionBar') public actionBarElement: ElementRef;
  @ViewChild('verifyHeader') public verifyHeader: ElementRef<HTMLElement>;
  @ViewChild('zonesContainer') public zonesContainerElement: ElementRef<HTMLElement>;
  @ViewChildren('colorListPreviewComponents') public colorListPreviewComponents: QueryList<ColorListPreviewWithPositionComponent>;

  public selectedZone: Zone;
  public distanceSpecifierOrientation = DistanceSpecifierOrientation;
  public canShowProductionScheduleVerify = false;
  public aspectRatio: ProductionScheduleBuilderAspectRatio = {width: 1, height: 1};
  public readonly scrollIndicatorDimensionsInPx: DimensionsInPx = {widthInPx: 33, heightInPx: 22};
  public horizontalPositionOfScrollIndicators: number;
  public productionScheduleVerifyViewportTopPosition = 0;
  public verticalPositionOfBottomScrollIndicator: number;
  public productionScheduleVerifyContainerHeight: number;
  public productionScheduleVerifyContainerWidth: number;
  public isEditPathsEnabled = false;
  public scrollLeftOffset: number;
  public canShowBottomScrollIndicator = false;
  public suggestedRestZones: RestZone[];
  public suggestedFreeZones: FreeZone[];
  public productionScheduleVerifyHeight = 0;
  public overlayRef: OverlayRef;
  public readonly permissionToViewBuggyLength = Permission.VIEW_MIN_MAX_BUGGY;
  public readonly productionScheduleVerifyMargin = {right: 25, left: 50};
  public readonly horizontalAxisHeightInPx = 35;

  private topScrollIndicatorListenerMouseEnterFn: () => void;
  private topScrollIndicatorListenerMouseClickFn: () => void;
  private bottomScrollIndicatorListenerMouseEnterFn: () => void;
  private bottomScrollIndicatorListenerMouseClickFn: () => void;
  private productionScheduleVerifySelection: d3Selection<BaseType, null, BaseType, null>;
  private productionScheduleVerifyVerticalAxisSelection: d3Selection<SVGGElement, null, SVGGElement, null>;
  private productionScheduleVerifyHorizontalAxisSelection: d3Selection<SVGGElement, null, SVGGElement, null>;

  private originalProductionScheduleVerifyHeight: number;
  private originalProductionScheduleVerifyWidth = 0;
  private currentHoveredProductionSchedulePathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent;
  private previousHoveredProductionSchedulePathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent;
  private targetPositionOfTopScrollIndicator;
  private targetPositionOfBottomScrollIndicator;

  private readonly maxZoomLevel = 5;
  private readonly buggyLengthTextOffset = 4;
  private readonly zoneIndicatorWidth = 16;
  private readonly zoneIndicatorLineWidthInPx = 2;
  private readonly approximateHorizontalTicksCount = 10;

  private debounceSubjectInitialise: Subject<void> = new Subject<void>();
  private debounceSubjectReinitialise: Subject<void> = new Subject<void>();

  public constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translate: TranslateService
  ) {
    super();
    this.initialiseDebounceSubjects();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('productionSchedule' in changes) {
      if (!changes.productionSchedule.isFirstChange()) {
        this.debounceSubjectInitialise.next();
      }
      this.setProductionScheduleZones();
    }

    if ('zoomLevel' in changes && !changes.zoomLevel.firstChange) {
      if (changes.zoomLevel.currentValue > changes.zoomLevel.previousValue) {
        this.canZoomIn.emit(Math.round(this.zoomLevel) !== this.maxZoomLevel);
        this.canZoomOut.emit(true);
      } else {
        this.canZoomIn.emit(true);
        this.canZoomOut.emit(
          this.getOriginProductionScheduleVerifyWidth() > this.productionScheduleVerifyContainerWidth || this.productionScheduleVerifyHeight > this.productionScheduleVerifyContainerHeight
        );
      }

      this.zoom();
    }

    if ('selectedProductionScheduleDisplay' in changes && !changes.selectedProductionScheduleDisplay.isFirstChange()) {
      this.onSelectedProductionScheduleDisplayChange();
    }

    if ('displayTechnicalUnit' in changes && !changes.displayTechnicalUnit.isFirstChange()) {
      this.updateHorizontalAxis();
    }

    this.debounceSubjectReinitialise.next();
  }

  public ngAfterViewInit(): void {
    this.reInitialiseProductionScheduleVerify();

    this.productionSchedulePlan.currentSelectedZone.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((zone: Zone) => {
      this.selectedZone = zone;
      if (AssertionUtils.isNullOrUndefined(this.selectedZone)) {
        this.setUpDimensionsForProductionScheduleVerify();
        setTimeout(() => {
          this.setAspectRatioAndUpdateProductionScheduleVerifyContainer();
        });
      }
    });
    this.productionSchedulePlan.currentEditingPaths.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((isEditingPaths: boolean) => (this.isEditPathsEnabled = isEditingPaths));

    this.observeElementResize();

    setTimeout(() => {
      this.getProductionScheduleVerifyHeight();
      this.reInitialiseProductionScheduleVerify();
    });

    this.finalReInitialiseProductionScheduleVerify();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    this.productionSchedulePlan.updateEditingPaths(false);

    if (!AssertionUtils.isNullOrUndefined(this.topScrollIndicatorListenerMouseClickFn)) {
      this.topScrollIndicatorListenerMouseClickFn();
    }
    if (!AssertionUtils.isNullOrUndefined(this.topScrollIndicatorListenerMouseEnterFn)) {
      this.topScrollIndicatorListenerMouseEnterFn();
    }
    if (!AssertionUtils.isNullOrUndefined(this.bottomScrollIndicatorListenerMouseClickFn)) {
      this.bottomScrollIndicatorListenerMouseClickFn();
    }
    if (!AssertionUtils.isNullOrUndefined(this.bottomScrollIndicatorListenerMouseEnterFn)) {
      this.bottomScrollIndicatorListenerMouseEnterFn();
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    setTimeout(() => {
      this.getProductionScheduleVerifyHeight();
    }, 1000);
  }

  public preventDefaultEvent(event: any): void {
    event.preventDefault();
  }

  public isProductionSchedulePhaseVerify(): boolean {
    return this.productionSchedulePhase === ProductionSchedulePhase.VERIFY;
  }

  public canShowRestZones(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.suggestedRestZones);
  }

  public canShowFreeZones(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.suggestedFreeZones);
  }

  public isZoneSelected(zone: Zone): boolean {
    return !AssertionUtils.isNullOrUndefined(zone) && this.selectedZone === zone;
  }

  public canShowZoneIndicator(zone: Zone): boolean {
    return this.isZoneSelected(zone) && !AssertionUtils.isNullOrUndefined(this.productionScheduleZoneComponent);
  }

  public getZoneIndicatorPosition(): number {
    let result = 0;

    if (!AssertionUtils.isNullOrUndefined(this.selectedZone)) {
      result =
        this.getZonePosition(this.selectedZone) +
        (this.getZoneWidth(this.selectedZone) - this.zoneIndicatorWidth + this.zoneIndicatorLineWidthInPx) / 2 -
        this.productionScheduleVerifyContainer.nativeElement.scrollLeft;
    }

    return result;
  }

  public getProductionScheduleVerifyWidth(): number {
    let result = 0;

    if (this.isProductionScheduleDisplayOverview()) {
      const {left, right} = this.productionScheduleVerifyMargin;
      result = this.productionScheduleVerifyContainerWidth - left - right;
    } else if (this.isProductionScheduleDisplayDetailView()) {
      result = this.getOriginProductionScheduleVerifyWidth();
    }

    return Number.isFinite(result) && !Number.isNaN(result) ? result : 0;
  }

  public onProductionSchedulePlanChanged(): void {
    this.productionSchedulePlanChanged.emit();
  }

  public getProductionScheduleVerifyMinBuggyLengthPosition(): number {
    return Math.max(0, this.originalProductionScheduleVerifyHeight - this.buggyLengthMinInPicks) * this.aspectRatio.height;
  }

  public getProductionScheduleVerifyMinBuggyLengthTextPositionY(): number {
    return Math.min(this.getProductionScheduleVerifyMinBuggyLengthPosition() + this.buggyLengthTextOffset, this.productionScheduleVerifyHeight);
  }

  public canShowProductionScheduleVerifyMinBuggyLengthIndicator(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.buggyLengthMinInPicks) && this.buggyLengthMinInPicks > 0 && this.originalProductionScheduleVerifyHeight >= this.buggyLengthMinInPicks;
  }

  public canShowProductionScheduleVerifyMaxBuggyLengthIndicator(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.buggyLengthMaxInPicks) && this.buggyLengthMaxInPicks > 0 && this.originalProductionScheduleVerifyHeight > this.buggyLengthMaxInPicks;
  }

  public getProductionScheduleVerifyMaxBuggyLengthPosition(): number {
    return Math.max(0, this.originalProductionScheduleVerifyHeight - this.buggyLengthMaxInPicks) * this.aspectRatio.height;
  }

  public getProductionScheduleVerifyMaxBuggyLengthTextPositionY(): number {
    return Math.min(this.getProductionScheduleVerifyMaxBuggyLengthPosition() + this.buggyLengthTextOffset, this.productionScheduleVerifyHeight);
  }

  public onScrollProductionScheduleVerifyContainer(event: Event): void {
    const {scrollTop, scrollLeft} = event.target as Element;

    this.scrollLeftOffset = scrollLeft;

    this.productionScheduleVerifyViewportTopPosition = scrollTop;

    this.verticalPositionOfBottomScrollIndicator =
      this.productionScheduleVerifyViewportTopPosition +
      this.productionScheduleVerifyContainer.nativeElement.clientHeight -
      this.horizontalAxisHeightInPx -
      this.getVerifyHeaderHeight() -
      this.scrollIndicatorDimensionsInPx.heightInPx;

    this.canShowBottomScrollIndicator = this.isHoveringProductionSchedulePath() && this.canScrollToBottom();
  }

  public getOriginProductionScheduleVerifyWidth(): number {
    let result: number;
    if (this.isProductionScheduleDisplayOverview()) {
      const {left} = this.productionScheduleVerifyMargin;
      result = this.productionScheduleVerifyContainerWidth - left;
    } else if (this.isProductionScheduleDisplayDetailView()) {
      result = this.originalProductionScheduleVerifyWidth * this.aspectRatio.width;
    }

    return isNaN(result) ? 0 : result;
  }

  public getProductionScheduleVerifyHeight(): void {
    let result = 0;

    if (this.isProductionScheduleDisplayOverview()) {
      result = this.productionScheduleVerifyContainerHeight - this.horizontalAxisHeightInPx - this.getVerifyHeaderHeight();
    } else if (this.isProductionScheduleDisplayDetailView()) {
      result = this.originalProductionScheduleVerifyHeight * this.aspectRatio.height;
    }

    this.productionScheduleVerifyHeight = AssertionUtils.isNumber(result) && !Number.isNaN(result) ? result : 0;
  }

  public canShowZone(zone: Zone): boolean {
    return this.getZoneWidth(zone) > 0;
  }

  public getZoneWidth(zone: Zone): number {
    return Math.min(this.getProductionScheduleVerifyWidth() - this.getZonePosition(zone), zone.technicalWidthInDents * this.aspectRatio.width);
  }

  public getZonePosition(zone: Zone): number {
    const maximumZonePosition = this.getProductionScheduleVerifyWidth() - this.zoneIndicatorLineWidthInPx;
    return Math.min((zone.startDent - ProductionScheduleVerifyComponent.dentOffset) * this.aspectRatio.width, maximumZonePosition);
  }

  public hoveredProductionSchedulePathComponent(components: {
    coloredYarnSetComponent: ProductionScheduleColoredYarnSetComponent;
    pathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent;
  }): void {
    this.previousHoveredProductionSchedulePathComponent = this.currentHoveredProductionSchedulePathComponent;
    this.currentHoveredProductionSchedulePathComponent = components.pathComponent;
    if (!AssertionUtils.isNullOrUndefined(components.pathComponent)) {
      this.horizontalPositionOfScrollIndicators = this.calculateHorizontalPositionOfScrollIndicators(components.coloredYarnSetComponent, components.pathComponent);
      this.targetPositionOfTopScrollIndicator = this.calculateTargetPositionOfTopScrollIndicator(components.pathComponent);
      this.targetPositionOfBottomScrollIndicator = this.calculateTargetPositionOfBottomScrollIndicator();

      this.changeDetectorRef.detectChanges();

      this.canShowBottomScrollIndicator = this.isHoveringProductionSchedulePath() && this.canScrollToBottom();

      if (this.canShowBottomScrollIndicator && !AssertionUtils.isNullOrUndefined(this.bottomScrollIndicator)) {
        this.setBottomScrollIndicatorEventHandlers();
      }
    }
    setTimeout(() => (this.previousHoveredProductionSchedulePathComponent = null));
  }

  public getProductionScheduleColoredYarnSetWidth(productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent): number {
    return productionScheduleColoredYarnSet.technicalWidthInDents * this.aspectRatio.width;
  }

  public getToolbarTopWidth(): number {
    return this.getContentWidth() - this.productionScheduleVerifyMargin.left;
  }

  public getContentWidth(): number {
    const {left, right} = this.productionScheduleVerifyMargin;
    let result = this.getOriginProductionScheduleVerifyWidth();

    if (this.isProductionScheduleDisplayOverview()) {
      result += right;
    } else if (this.isProductionScheduleDisplayDetailView()) {
      result += left;
    }

    return result;
  }

  public getProductionScheduleVerifyHorizontalAxisWidth(): number {
    let result = this.getOriginProductionScheduleVerifyWidth();
    result -= this.productionScheduleVerifyMargin.right;

    return !isNaN(result) ? result : 0;
  }

  public getZoneIndicatorLineHorizontalPosition(zone: Zone): number {
    return (this.getZoneWidth(zone) + this.zoneIndicatorLineWidthInPx) / 2;
  }

  public trackByProductionSchedulePathsOfColoredYarnSet = (index: number, productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): number =>
    productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent;

  public canShowMinimumBuggyLengthWarningForProductionSchedulePath(productionSchedulePath: ProductionSchedulePath): boolean {
    return productionSchedulePath.totalLengthInPicks < this.buggyLengthMinInPicks;
  }

  public canShowMaximumBuggyLengthWarningForProductionSchedulePath(productionSchedulePath: ProductionSchedulePath): boolean {
    return productionSchedulePath.totalLengthInPicks > this.buggyLengthMaxInPicks;
  }

  public canShowMaximumBuggyLengthWarningForProductionSchedulePathsOfColoredYarnSet(productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): boolean {
    return (
      this.productionSchedulePhase === ProductionSchedulePhase.BUILD || (this.isProductionSchedulePhaseVerify() && AssertionUtils.isNullOrUndefined(productionSchedulePathsOfColoredYarnSet.errors))
    );
  }

  public canShowFreeAndRestZonesEditor(): boolean {
    return this.isProductionSchedulePhaseVerify() && this.productionSchedule.machine.canHaveRestAndFreeZonesAndPathLabels();
  }

  public isProductionScheduleDisplayOverview(): boolean {
    return this.selectedProductionScheduleDisplay === ProductionScheduleDisplay.OVERVIEW;
  }

  public getWidthInDentsWithAspectRatio(technicalWidthInDents: number): number {
    return technicalWidthInDents * this.aspectRatio.width;
  }

  public isProductionSchedulePath(item: ProductionSchedulePath | RestZone | FreeZone): boolean {
    return item instanceof ProductionSchedulePath;
  }

  public getProductionSchedulePathLabel(productionSchedulePath: ProductionSchedulePath, width: number): string {
    if (width > 230) {
      return `${this.translate.instant('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.PATH')} ${productionSchedulePath.pathNumber}`;
    } else {
      return `P${productionSchedulePath.pathNumber}`;
    }
  }

  public isRestZone(item: ProductionSchedulePath | RestZone | FreeZone): boolean {
    return item instanceof RestZone;
  }

  public getRestZoneTooltip(item: RestZone): string {
    return [
      this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.REST_ZONE.REST_ZONE', {count: 1}) + ':',
      item.technicalWidthInDents,
      this.translate.instant('GENERAL.UNIT.DENTS'),
      '/',
      convertCommercialUnit({
        from: {
          unit: Unit.METER,
          value: convertDentsToWidthInMeter(item.technicalWidthInDents, this.productionSchedule.machineQuality.reedDensity * 1000)
        },
        to: this.commercialUnit
      }) + this.commercialUnit,
      'x',
      this.buggyLengthMaxInPicks,
      this.translate.instant('GENERAL.UNIT.PICKS'),
      '/',
      convertCommercialUnit({
        from: {
          unit: Unit.MILLIMETER,
          value: this.buggyLengthMaxInMM
        },
        to: this.commercialUnit
      }) + this.commercialUnit
    ].join(' ');
  }

  public getRestLabelForProductionSchedulePathsOfColoredYarnSet(productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): string {
    let label = `${this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN_SUMMARY.REST_LENGTH')} `;

    if (this.displayTechnicalUnit) {
      const maxProductionSchedulePathInPicks = productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.reduce((maxPath: ProductionSchedulePath, currentPath: ProductionSchedulePath) => {
        return currentPath.totalLengthInPicks > (maxPath?.totalLengthInPicks || 0) ? currentPath : maxPath;
      }, null);

      const productionScheduleHeightInPicks = maxProductionSchedulePathInPicks ? Math.max(maxProductionSchedulePathInPicks.totalLengthInPicks, this.buggyLengthMinInPicks) : this.buggyLengthMinInPicks;

      const remainingWidthInDents =
        productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.technicalWidthInDents - productionSchedulePathsOfColoredYarnSet.getProductionSchedulePathsWidthInDents();

      label += this.getLabelForTechnicalDimensions(remainingWidthInDents, Math.round(productionScheduleHeightInPicks) || 0);
    } else {
      const remainingWidthInMillimeters =
        convertDentsToWidthInMeter(productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents, this.productionSchedule.machineQuality.reedDensity * 1000) -
        productionSchedulePathsOfColoredYarnSet.getProductionSchedulePathsWidthInMillimeters();

      const maxProductionSchedulePathInMM = productionSchedulePathsOfColoredYarnSet.productionSchedulePaths.reduce((maxPath: ProductionSchedulePath, currentPath: ProductionSchedulePath) => {
        return currentPath.totalLengthInMM > (maxPath?.totalLengthInMM || 0) ? currentPath : maxPath;
      }, null);

      const buggyLengthMinInMeters = convertPicksToHeightInMeter(this.buggyLengthMinInPicks, this.productionSchedule.machineQuality.pickDensity * 1000);
      const buggyLengthMinInMillimeters = convertCommercialUnit({from: {unit: Unit.METER, value: buggyLengthMinInMeters}, to: Unit.MILLIMETER});

      const productionScheduleHeightInMM = maxProductionSchedulePathInMM ? Math.max(maxProductionSchedulePathInMM.totalLengthInMM, buggyLengthMinInMillimeters) : buggyLengthMinInMillimeters;

      const lengthInCommercialUnit = this.convertValueToCommercialUnit(productionScheduleHeightInMM, Unit.MILLIMETER);
      const widthInCommercialUnit = this.convertValueToCommercialUnit(remainingWidthInMillimeters, Unit.MILLIMETER);

      label += this.getLabelForCommercialDimensions(widthInCommercialUnit, lengthInCommercialUnit);
    }

    return label;
  }

  public getMinimumProductionScheduleVerifyTechnicalHeight(): number {
    const longestPathLengthInPicks = getLongestProductionSchedulePath(this.productionSchedule?.productionSchedulePathsOfColoredYarnSets);
    let currentAvailableSpaceForSchedule: number;

    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
      currentAvailableSpaceForSchedule = this.availableSpaceInBuggyInPicks + longestPathLengthInPicks;
    }

    if (!AssertionUtils.isNullOrUndefined(currentAvailableSpaceForSchedule) && !isNaN(currentAvailableSpaceForSchedule) && currentAvailableSpaceForSchedule < this.buggyLengthMaxInPicks) {
      return Math.max(currentAvailableSpaceForSchedule, longestPathLengthInPicks);
    } else {
      return Math.max(longestPathLengthInPicks, this.buggyLengthMaxInPicks);
    }
  }

  public getMinimumProductionScheduleVerifyInMM(): number {
    const longestPathLengthInMM = this.getLongestPathLengthInMM();
    const availableSpaceInMM = convertCommercialUnit({
      from: {
        value: this.availableSpaceInBuggyInCommercialUnit ?? 0,
        unit: Unit.CENTIMETER
      },
      to: Unit.MILLIMETER
    });
    let currentAvailableSpaceForSchedule: number;

    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
      currentAvailableSpaceForSchedule = Math.round(availableSpaceInMM + longestPathLengthInMM);
    }

    if (!AssertionUtils.isNullOrUndefined(currentAvailableSpaceForSchedule) && !isNaN(currentAvailableSpaceForSchedule) && currentAvailableSpaceForSchedule < this.buggyLengthMaxInMM) {
      return Math.max(currentAvailableSpaceForSchedule, longestPathLengthInMM);
    } else {
      return Math.max(longestPathLengthInMM, this.buggyLengthMaxInMM);
    }
  }

  public getProductionScheduleVerifyTechnicalWidth(): number {
    return this.productionSchedule.machine.dentsForFabric;
  }

  public getProductionScheduleVerifyCommercialWidth(): number {
    return convertDentsToWidthInMeter(this.productionSchedule.machine.dentsForFabric, this.productionSchedule.machineQuality.reedDensity * 1000) * 1000;
  }

  public canShowHeader(width: number): boolean {
    return width > 100;
  }

  public canShowTotalHeight(width: number): boolean {
    return width > 100;
  }

  public getFlexDirection(width: number): string {
    return width > 320 ? 'flex-row' : 'flex-column';
  }

  public getTotalHeight(item: ProductionSchedulePath, width: number): string {
    const totalHeightInCommercialUnit = Math.ceil(
      convertCommercialUnit({
        from: {
          value: item.totalLengthInMM ?? 0,
          unit: Unit.MILLIMETER
        },
        to: this.commercialUnit
      })
    );

    if (width > 150) {
      return `${item.totalLengthInPicks} ${this.translate.instant('GENERAL.UNIT.PICKS')} / ${totalHeightInCommercialUnit} ${this.translate.instant('GENERAL.UNIT.CENTIMETER')}`;
    } else {
      return `${item.totalLengthInPicks} / ${totalHeightInCommercialUnit}`;
    }
  }

  private reInitialiseProductionScheduleVerify(): void {
    setTimeout(() => {
      this.initialiseProductionScheduleVerifyContainerDimensions();
      this.initialiseProductionScheduleVerify();
    });
  }

  private zoom(): void {
    this.setAspectRatio();
    this.updateHorizontalAndVerticalAxis();
  }

  private isHoveringProductionSchedulePath(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.currentHoveredProductionSchedulePathComponent) || !AssertionUtils.isNullOrUndefined(this.previousHoveredProductionSchedulePathComponent);
  }

  private canScrollToTop(): boolean {
    return this.productionScheduleVerifyContainer.nativeElement.scrollTop > this.targetPositionOfTopScrollIndicator;
  }

  private canScrollToBottom(): boolean {
    return this.productionScheduleVerifyContainer.nativeElement.scrollTop < this.targetPositionOfBottomScrollIndicator;
  }

  private initialiseProductionScheduleVerifyContainerDimensions(): void {
    this.productionScheduleVerifyContainerHeight = this.elementRef.nativeElement.clientHeight - this.getActionBarHeight() - this.getZonesContainerHeight() - 60;
    this.productionScheduleVerifyContainerWidth = this.elementRef.nativeElement.clientWidth;
  }

  private initialiseProductionScheduleVerify(): void {
    this.setUpDimensionsForProductionScheduleVerify();

    setTimeout(() => {
      this.initialiseSelections();
      this.setAspectRatioAndUpdateProductionScheduleVerifyContainer();
      this.scrollToBottomOfProductionScheduleVerify();
    });
  }

  private setUpDimensionsForProductionScheduleVerify(): void {
    this.setOriginalProductionScheduleVerifyHeight();

    this.originalProductionScheduleVerifyWidth = this.productionSchedule.getTotalProductionScheduleWidthInDents();

    this.canShowProductionScheduleVerify = true;
  }

  private calculateHorizontalPositionOfScrollIndicators(
    coloredYarnSetComponent: ProductionScheduleColoredYarnSetComponent,
    productionSchedulePathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent
  ): number {
    return (
      coloredYarnSetComponent.getProductionScheduleColoredYarnSetLeft() +
      productionSchedulePathComponent.getProductionSchedulePathLeft() +
      (productionSchedulePathComponent.getProductionSchedulePathWidth() - this.scrollIndicatorDimensionsInPx.widthInPx) / 2
    );
  }

  private calculateTargetPositionOfTopScrollIndicator(productionSchedulePathComponent: ProductionSchedulePathOfColoredYarnSetNewComponent): number {
    return Math.floor(productionSchedulePathComponent.getProductionSchedulePathTop());
  }

  private calculateTargetPositionOfBottomScrollIndicator(): number {
    return this.productionScheduleVerifyContainer.nativeElement.scrollHeight - this.productionScheduleVerifyContainer.nativeElement.clientHeight;
  }

  private getLongestPathLengthInMM(): number {
    let longestPathLengthInMM = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
        longestPathLengthInMM = Math.max(longestPathLengthInMM, productionSchedulePath?.totalLengthInMM);
      });
    });

    return longestPathLengthInMM;
  }

  private setTopScrollIndicatorEventHandlers(): void {
    this.topScrollIndicatorListenerMouseEnterFn = this.renderer.listen(this.topScrollIndicator.nativeElement, 'mouseenter', () => {
      this.currentHoveredProductionSchedulePathComponent = this.previousHoveredProductionSchedulePathComponent;
    });
    this.topScrollIndicatorListenerMouseClickFn = this.renderer.listen(this.topScrollIndicator.nativeElement, 'click', () => {
      this.productionScheduleVerifyContainer.nativeElement.scrollTo({
        left: this.productionScheduleVerifyContainer.nativeElement.scrollLeft,
        top: this.targetPositionOfTopScrollIndicator
      });
    });
  }

  private setBottomScrollIndicatorEventHandlers(): void {
    this.bottomScrollIndicatorListenerMouseEnterFn = this.renderer.listen(this.bottomScrollIndicator.nativeElement, 'mouseenter', () => {
      this.currentHoveredProductionSchedulePathComponent = this.previousHoveredProductionSchedulePathComponent;
    });
    this.bottomScrollIndicatorListenerMouseClickFn = this.renderer.listen(this.bottomScrollIndicator.nativeElement, 'click', () => {
      this.productionScheduleVerifyContainer.nativeElement.scrollTo({
        left: this.productionScheduleVerifyContainer.nativeElement.scrollLeft,
        top: this.targetPositionOfBottomScrollIndicator
      });
    });
  }

  private initialiseSelections(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleVerify)) {
      this.productionScheduleVerifySelection = d3Select(this.productionScheduleVerify?.nativeElement);
    }

    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleVerifyVerticalAxis)) {
      this.productionScheduleVerifyVerticalAxisSelection = d3Select(this.productionScheduleVerifyVerticalAxis.nativeElement);
    }
  }

  private setAspectRatioAndUpdateProductionScheduleVerifyContainer(): void {
    this.setAspectRatio();
    this.updateHorizontalAndVerticalAxis();
  }

  private getTickLabelTranslationWithUnit(value: number, unit: Unit): string {
    return `${value.toString(10)} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, unit)}`)}`;
  }

  private getTickFormatForHorizontalAxis(value: number, index: number, numberOfTicks: number): string {
    if (this.displayTechnicalUnit) {
      return this.getHorizontalTickFormat(value, Unit.DENTS, numberOfTicks, index);
    } else {
      const valueInMeters = convertDentsToWidthInMeter(value, this.productionSchedule.machineQuality.reedDensity * 1000);
      const valueInCommercialUnit = this.convertValueToCommercialUnit(valueInMeters, Unit.METER);
      return this.getHorizontalTickFormat(valueInCommercialUnit, this.commercialUnit, numberOfTicks, index);
    }
  }

  private getHorizontalTickFormat(value: number, unit: Unit, numberOfTicks: number, index: number): string {
    const tickLabelTranslationWithUnit = this.getTickLabelTranslationWithUnit(value, unit);
    return numberOfTicks <= 1 || index === numberOfTicks - 1 ? tickLabelTranslationWithUnit : value.toString(10);
  }

  private updateHorizontalAxis(): void {
    let width = this.getOriginProductionScheduleVerifyWidth();

    if (this.isProductionScheduleDisplayOverview()) {
      width -= this.productionScheduleVerifyMargin.right;
    }

    const horizontalAxis = d3ScaleLinear().domain([1, this.originalProductionScheduleVerifyWidth]).range([0, width]);
    const numberOfTicks = horizontalAxis.ticks(this.approximateHorizontalTicksCount).length;

    d3AxisBottom(horizontalAxis)
      .tickSizeInner(10)
      .tickValues(horizontalAxis.ticks(this.approximateHorizontalTicksCount))
      .tickPadding(8)
      .tickSizeOuter(0)
      .tickFormat((value: number, index: number) => this.getTickFormatForHorizontalAxis(value, index, numberOfTicks))(this.productionScheduleVerifyHorizontalAxisSelection);
  }

  private updateHorizontalAndVerticalAxis(): void {
    const canUpdateHorizontalAndVerticalAxis =
      !AssertionUtils.isNullOrUndefined(this.productionScheduleVerifyVerticalAxisSelection) && !AssertionUtils.isNullOrUndefined(this.productionScheduleVerifyHorizontalAxisSelection);

    if (canUpdateHorizontalAndVerticalAxis) {
      this.updateHorizontalAxis();
      this.resizeColorListPreviewComponents();
    }
  }

  private scrollToBottomOfProductionScheduleVerify(): void {
    this.changeDetectorRef.detectChanges();

    this.productionScheduleVerifyContainer.nativeElement.scrollTo({
      left: 0,
      top: this.productionScheduleVerifyContainer.nativeElement.scrollHeight
    });
  }

  private setAspectRatio(): void {
    if (this.isProductionScheduleDisplayOverview()) {
      this.aspectRatio = {
        width: MathUtils.divide(this.getProductionScheduleVerifyWidth(), this.originalProductionScheduleVerifyWidth),
        height: MathUtils.divide(this.productionScheduleVerifyHeight, this.originalProductionScheduleVerifyHeight)
      };
    } else if (this.isProductionScheduleDisplayDetailView()) {
      this.aspectRatio = {
        width: this.zoomLevel,
        height: this.zoomLevel
      };
    }
  }

  private resizeColorListPreviewComponents(): void {
    this.colorListPreviewComponents.toArray().forEach((colorListPreviewComponent: ColorListPreviewWithPositionComponent) => {
      colorListPreviewComponent.calculateCreelPositionsToShow();
    });
  }

  private isProductionScheduleDisplayDetailView(): boolean {
    return this.selectedProductionScheduleDisplay === ProductionScheduleDisplay.DETAILVIEW;
  }

  private setProductionScheduleZones(): void {
    if (this.isProductionSchedulePhaseVerify()) {
      if (AssertionUtils.isNullOrUndefined(this.suggestedRestZones)) {
        this.suggestedRestZones = cloneDeep(this.productionSchedule.restZones);
      }

      if (AssertionUtils.isNullOrUndefined(this.suggestedFreeZones)) {
        this.suggestedFreeZones = cloneDeep(this.productionSchedule.freeZones);
      }
    } else {
      this.suggestedRestZones = null;
      this.suggestedFreeZones = null;
    }
  }

  private getVerifyHeaderHeight(): number {
    return !AssertionUtils.isNullOrUndefined(this.verifyHeader) ? this.verifyHeader.nativeElement.clientHeight : 0;
  }

  private getActionBarHeight(): number {
    return !AssertionUtils.isNullOrUndefined(this.actionBarElement) ? this.actionBarElement.nativeElement.clientHeight : 0;
  }

  private onSelectedProductionScheduleDisplayChange(): void {
    if (!AssertionUtils.isNullOrUndefined(this.productionScheduleVerifySelection)) {
      this.setAspectRatio();
      this.reInitialiseProductionScheduleVerify();
    }
  }

  private setOriginalProductionScheduleVerifyHeight(): void {
    this.originalProductionScheduleVerifyHeight = this.getMinimumProductionScheduleVerifyTechnicalHeight();
  }

  private observeElementResize(): void {
    let resizeObserver: ResizeObserver;

    new Observable((observer: Observer<void>) => {
      resizeObserver = new ResizeObserver(() => {
        if (this.selectedProductionScheduleDisplay === ProductionScheduleDisplay.OVERVIEW && this.canShowProductionScheduleVerify) {
          observer.next();
        }
      });
      resizeObserver.observe(this.elementRef.nativeElement);
    })
      .pipe(debounceTime(500), takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: () => this.onSelectedProductionScheduleDisplayChange(),
        complete: () => resizeObserver.disconnect()
      });
  }

  private getZonesContainerHeight(): number {
    return this.zonesContainerElement?.nativeElement.clientHeight ?? 0;
  }

  private convertValueToCommercialUnit(value: number, fromUnit: Unit): number {
    const result = convertCommercialUnit({
      from: {
        value: value ?? 0,
        unit: fromUnit
      },
      to: this.commercialUnit
    });

    return Math.round(result * 100) / 100;
  }

  private getLabelForTechnicalDimensions(technicalWidthInDents: number, technicalHeightInPicks: number): string {
    return `(${technicalWidthInDents} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.DENTS)}`)} X ${technicalHeightInPicks} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.PICKS)}`
    )})`;
  }

  private getLabelForCommercialDimensions(commercialWidthInCommercialUnit: number, commercialHeightInCommercialUnit: number): string {
    return `(${commercialWidthInCommercialUnit} X ${commercialHeightInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)})`;
  }

  private finalReInitialiseProductionScheduleVerify(): void {
    setTimeout(() => {
      this.initialiseProductionScheduleVerifyContainerDimensions();
      this.getProductionScheduleVerifyHeight();
    }, 1000);
  }

  private initialiseDebounceSubjects(): void {
    this.debounceSubjectInitialise.pipe(debounceTime(300)).subscribe(() => {
      this.setOriginalProductionScheduleVerifyHeight();
      this.setAspectRatioAndUpdateProductionScheduleVerifyContainer();
    });

    this.debounceSubjectReinitialise.pipe(debounceTime(300)).subscribe(() => {
      setTimeout(() => {
        this.reInitialiseProductionScheduleVerify();
        this.getProductionScheduleVerifyHeight();
      }, 100);
    });
  }
}
